import { Controller } from "@hotwired/stimulus"

const palette = [
  { border: "#19a66d", background: "rgba(25, 166, 109, 0.2)" },
  { border: "#009ef6", background: "rgba(0, 158, 246, 0.2)" },
  { border: "#ff6f00", background: "rgba(255, 111, 0, 0.2)" }
]

export default class extends Controller {
  static targets = ["canvas", "state"]
  static values = {
    url: String,
    type: String,
    stacked: Boolean,
    currency: String,
    emptyMessage: String,
    loadingMessage: String
  }

  connect() {
    this.abortController = new AbortController()
    this.setState(this.loadingMessageValue)
    this.scheduleLoad()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }

    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  scheduleLoad() {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => this.loadChart())
    } else {
      setTimeout(() => this.loadChart(), 0)
    }
  }

  async loadChart() {
    try {
      const [{ default: Chart }, response] = await Promise.all([
        import("chart.js/auto"),
        fetch(this.urlValue, { headers: { Accept: "application/json" }, signal: this.abortController.signal })
      ])

      if (!response.ok) {
        throw new Error(`Chart request failed with status ${response.status}`)
      }

      const payload = await response.json()

      if (!payload.labels || payload.labels.length === 0) {
        this.setState(this.emptyMessageValue)
        return
      }

      this.setState("")
      const chartType = this.typeValue || payload.type || "line"

      this.chart = new Chart(this.canvasTarget.getContext("2d"), {
        type: chartType,
        data: {
          labels: payload.labels,
          datasets: this.decorateDatasets(payload.datasets || [], chartType)
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          interaction: { mode: "index", intersect: false },
          layout: { padding: { top: 4, right: 6, left: 6, bottom: 0 } },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                boxHeight: 8,
                boxWidth: 8,
                color: "#475569",
                font: { size: 11, weight: 600 },
                padding: 16,
                usePointStyle: true
              }
            },
            tooltip: this.tooltipOptions()
          },
          scales: {
            x: {
              stacked: this.stackedValue,
              border: { display: false },
              grid: { display: false },
              ticks: {
                color: "#94a3b8",
                font: { size: 10, weight: 600 },
                maxRotation: 0
              }
            },
            y: {
              beginAtZero: true,
              border: { display: false },
              grid: {
                color: "rgba(148, 163, 184, 0.25)",
                borderDash: [4, 4],
                drawTicks: false
              },
              ticks: {
                color: "#94a3b8",
                font: { size: 10, weight: 600 },
                padding: 8,
                precision: 0,
                callback: this.tickFormatter()
              },
              stacked: this.stackedValue
            }
          }
        }
      })
    } catch (error) {
      if (error.name === "AbortError") return
      this.setState(this.emptyMessageValue)
    }
  }

  decorateDatasets(datasets, chartType) {
    const isLineChart = chartType === "line"
    const isBarChart = chartType === "bar"
    const isStackedBar = isBarChart && this.stackedValue

    return datasets.map((dataset, index) => {
      const colors = palette[index % palette.length]
      const isTopStackSegment = isStackedBar && index === datasets.length - 1

      return {
        ...dataset,
        borderColor: colors.border,
        backgroundColor: isLineChart ? "rgba(25, 166, 109, 0.08)" : colors.background,
        borderWidth: isLineChart ? 2.5 : 1,
        tension: isLineChart ? 0.35 : 0,
        fill: isLineChart,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: colors.border,
        pointBorderWidth: 2,
        pointRadius: isLineChart ? 2.5 : 0,
        pointHoverRadius: isLineChart ? 4 : 0,
        pointHoverBorderWidth: 2,
        pointHitRadius: isLineChart ? 10 : 0,
        borderRadius: isBarChart ? (isStackedBar ? (isTopStackSegment ? { topLeft: 6, topRight: 6 } : 0) : 6) : undefined,
        borderSkipped: isBarChart ? (isStackedBar ? (isTopStackSegment ? false : "middle") : false) : undefined,
        maxBarThickness: chartType === "bar" ? 18 : undefined
      }
    })
  }

  setState(message) {
    if (!this.hasStateTarget) return
    this.stateTarget.textContent = message
    this.stateTarget.hidden = message === ""
    this.stateTarget.classList.toggle("is-hidden", message === "")
  }

  tickFormatter() {
    if (!this.hasCurrencyValue || !this.currencyValue) return undefined

    return (value) => this.formatCurrency(value)
  }

  tooltipOptions() {
    if (!this.hasCurrencyValue || !this.currencyValue) {
      return {
        mode: "index",
        intersect: false,
        backgroundColor: "#0f172a",
        borderColor: "#1e293b",
        borderWidth: 1,
        bodyColor: "#f8fafc",
        titleColor: "#f8fafc",
        padding: 10
      }
    }

    return {
      mode: "index",
      intersect: false,
      backgroundColor: "#0f172a",
      borderColor: "#1e293b",
      borderWidth: 1,
      bodyColor: "#f8fafc",
      padding: 10,
      titleColor: "#f8fafc",
      callbacks: {
        label: (context) => {
          const label = context.dataset.label || ""
          const value = this.formatCurrency(context.parsed.y)
          return label ? `${label}: ${value}` : value
        }
      }
    }
  }

  formatCurrency(value) {
    try {
      const currency = (this.currencyValue || "").toUpperCase()
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency
      }).format(value)
    } catch (error) {
      return value
    }
  }
}
